import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject, map, of, shareReplay, catchError } from 'rxjs';
import { environment } from '../../../environments/environment';

export type SettingType = 'int' | 'decimal' | 'boolean' | 'string' | 'select';
export type SettingValue = number | boolean | string | null;

export interface SettingOption {
  value: string;
  label: string;
}

export interface SettingGroup {
  key: string;
  label: string;
  description: string;
  /** Bootstrap-icon name without the `bi-` prefix. */
  icon: string;
  sortOrder: number;
}

export interface AppSetting {
  key: string;
  label: string;
  description: string;
  group: string;
  type: SettingType;
  defaultValue: SettingValue;
  sortOrder: number;
  min?: number;
  max?: number;
  decimals?: number;
  maxLength?: number;
  options?: SettingOption[];
  nullable?: boolean;
  nullLabel?: string;
  unit?: string;
  placeholder?: string;
  /** Effective value for this client (stored value, or default when unset). */
  value: SettingValue;
  isOverridden: boolean;
  /** Licensed-module switch this user may not change (managed by Zenflow). */
  locked?: boolean;
}

export interface SettingsResponse {
  groups: SettingGroup[];
  settings: AppSetting[];
}

/** Keys mirror backend src/settings/setting-registry.ts. */
export const SETTING_KEYS = {
  /**
   * Which SKUs a purchase order may carry for its vendor.
   *   STRICT   only SKUs mapped to the vendor can be added (default).
   *   PROMPT   any SKU; on submit the buyer is asked which unmapped lines to
   *            map to the vendor — the rest are posted unmapped.
   *   REQUIRED any SKU; every line must be mapped (or removed) before submit.
   */
  PO_VENDOR_SKU_MAPPING_MODE: 'po.vendorSkuMappingMode',
  /** Governs both the GRN invoice date and the GRN received date. */
  GRN_BACKDATE_LIMIT_DAYS: 'grn.backdateLimitDays',
  /**
   * When true, the GRN form shows the Received Date picker. When false the
   * field is hidden and the server stamps the day the GRN is posted.
   */
  GRN_ALLOW_RECEIVED_DATE_ENTRY: 'grn.allowReceivedDateEntry',
  /**
   * When true, a POSTED GRN stays editable and the correction is applied to
   * stock as a delta. When false, only DRAFT GRNs can be edited.
   */
  GRN_ALLOW_EDIT_POSTED: 'grn.allowEditPosted',
  /**
   * When true, an Admin may cancel a posted GRN: the whole receipt is reversed
   * out of stock and the document is marked CANCELLED.
   */
  GRN_ALLOW_CANCEL_POSTED: 'grn.allowCancelPosted',
  /**
   * When true, a warehouse-sourced Transfer Out or a warehouse bulk breakup
   * may post past a stock shortfall — warehouse inventory goes negative
   * instead of blocking.
   */
  ALLOW_NEGATIVE_WAREHOUSE_STOCK: 'inventory.allowNegativeWarehouseStock',
  /** MANUAL: SKUs typed on the Bulk Add Products grid; AUTO: generated on save. */
  PRODUCT_SKU_ENTRY_MODE: 'products.skuEntryMode',
  /** When true (default) a new product must name a primary vendor. */
  PRODUCT_VENDOR_REQUIRED: 'products.vendorRequired',
  /** When true a new product must name a manufacturer. Off by default. */
  PRODUCT_MANUFACTURER_REQUIRED: 'products.manufacturerRequired',
  /** When true a new product must name a brand. Off by default. */
  PRODUCT_BRAND_REQUIRED: 'products.brandRequired',
  /**
   * When true the product form lists only the picked manufacturer's brands and
   * a new brand needs a manufacturer. Off by default: picked/added independently.
   */
  PRODUCT_BRAND_LINKED_TO_MANUFACTURER: 'products.brandLinkedToManufacturer',
  /**
   * When true the box-label print screen keeps its Print buttons after a
   * batch has been printed (reprint). Off by default: a duplicate QR label
   * would let two physical boxes share one identity.
   */
  TRANSFER_BOX_ALLOW_LABEL_REPRINT: 'transferBox.allowLabelReprint',
  /** Consignment value (incl. GST) above which a Transfer Out needs an e-way bill. */
  EWAY_BILL_THRESHOLD_VALUE: 'ewaybill.thresholdValue',
  /** Judge TOs dispatched together as a group against the threshold. */
  EWAY_BILL_AGGREGATE_DISPATCH: 'ewaybill.aggregateDispatch',
  /** When true, one stock take session may be merged on its own into a master. */
  STOCKTAKE_ALLOW_SINGLE_SESSION_MERGE: 'stocktake.allowSingleSessionMerge',
  /** Brand printed first on every POS receipt header (all branches); blank = store name. */
  BILLING_RECEIPT_BRAND_NAME: 'billing.receiptBrandName',

  // B2B Sales module (see backend setting-registry.ts, group B2B_SALES)
  /** Master switch: shows the B2B Sales menu and opens the B2B APIs. */
  B2B_ENABLED: 'b2b.enabled',
  /** Prefix for generated B2B order / invoice numbers. */
  B2B_ORDER_NUMBER_PREFIX: 'b2b.orderNumberPrefix',
  /** When true an admin may give a B2B order a past business date (with a reason). */
  B2B_ALLOW_ORDER_BACKDATING: 'b2b.allowOrderBackdating',
  /** When true the backdating lower cap is 1 April of the FY instead of the 1st of the month. */
  B2B_ALLOW_BACKDATE_ACROSS_FY: 'b2b.allowBackdateAcrossFY',
  /** YYYY-MM-DD; no B2B order may be dated on or before it. Blank = no lock. */
  B2B_GST_RETURNS_FILED_UPTO: 'b2b.gstReturnsFiledUpto',
} as const;

@Injectable({ providedIn: 'root' })
export class ConfigurationService {
  private http = inject(HttpClient);
  private base = `${environment.apiUrl}/settings`;

  /**
   * Cached because operational screens (e.g. Create GRN) read a single value on
   * load. Cleared after a save so the Configuration page never serves stale data.
   */
  private settings$?: Observable<SettingsResponse>;

  /** Emits after every successful save so cached consumers can refresh. */
  readonly changed$ = new Subject<void>();

  getSettings(): Observable<SettingsResponse> {
    if (!this.settings$) {
      this.settings$ = this.http
        .get<SettingsResponse>(this.base)
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));
    }
    return this.settings$;
  }

  saveSettings(
    settings: { key: string; value: SettingValue }[]
  ): Observable<SettingsResponse> {
    return this.http
      .put<SettingsResponse>(this.base, { settings })
      .pipe(
        map((res) => ((this.settings$ = undefined), res)),
        map((res) => (this.changed$.next(), res))
      );
  }

  /**
   * Single value lookup for feature screens. Resolves to null (= not set) if the
   * settings call fails, so a config outage never blocks data entry — the server
   * still enforces the real rule on submit.
   */
  getValue(key: string): Observable<SettingValue> {
    return this.getSettings().pipe(
      map((res) => res.settings.find((s) => s.key === key)?.value ?? null),
      catchError(() => of(null))
    );
  }
}
